import dayjs from 'dayjs';
import {
  CollectionPurpose,
  CollectionStatus,
  EscalationStatus,
  HandoverStatus,
  RefundPayoutStatus,
  REFUND_PAYOUT_STATUS_LABEL,
  HandoverTimelineEntry,
  isAbnormalTermination,
  RentalArrears,
  RentalDetail,
  RentalDocuments,
  RentalDocumentStage,
  RentalListItem,
  RentalStatus,
  RentalReassignment,
  RentalTimelineEntry,
  RentalVehicle,
  RentalWaiver,
  TimelineStatusType,
} from '@/types/rental';
// Straight from the modules rather than the `@/utils` and `@/components`
// barrels: both reach the axios client, which drags an ESM-only dependency into
// every Jest suite that touches this file.
import { describePackageVehicleReason } from '@components/PackageVehicleReasons/packageVehicleReasons';
import { formatRupiah } from '@/utils/format';
import type { CorrectedDocument } from '@/services/api/opsHandovers';

/**
 * Status key for the `rentalStatus` Status variant. A rental that ENDED *badly*
 * reads as "Terminated" — the label the design shows, which is a display
 * concern rather than an API status (TRD B0).
 *
 * Keyed on ENDED, not COMPLETED. COMPLETED used to mean "the unit came back";
 * it now means an RTO tenor filled and the driver owns the bike, which is the
 * best possible outcome and must never render as Terminated.
 *
 * The termination reason has to be inspected, not merely tested for presence:
 * the backend stamps `terminationReason: 'COMPLETED'` on every normal return,
 * so `status === ENDED && terminationReason` is true for the happy path as well
 * and would paint every finished rental with the red Terminated pill.
 */
export const resolveRentalStatusKey = (
  rental: Pick<RentalListItem, 'status' | 'terminationReason'>
): string => {
  if (
    rental.status === RentalStatus.ENDED &&
    isAbnormalTermination(rental.terminationReason)
  ) {
    return 'TERMINATED';
  }
  return rental.status;
};

/**
 * Whether the escalation ladder is worth a pill. NORMAL means nothing is
 * wrong, and a pill for "nothing is wrong" is noise that dilutes the rungs
 * that do carry a consequence.
 */
/**
 * The repo-eligibility gate, mirrored from POST /v1/repossessions: the ladder
 * said it's repo time (or the unit is already shut down) AND the unit is out
 * with the driver — the only state the create endpoint accepts a case for.
 * One definition for the list action, the detail button, and the ?action=
 * deep link, so the three can never drift. An unclosed case is linked instead.
 */
export const canRequestRepossession = (
  rental: Pick<
    RentalListItem,
    'handoverStatus' | 'escalationStatus' | 'repossession'
  >
): boolean =>
  !rental.repossession &&
  rental.handoverStatus === HandoverStatus.ACTIVE &&
  (rental.escalationStatus === EscalationStatus.REPO_REQUESTED ||
    rental.escalationStatus === EscalationStatus.SHUTDOWN);

/**
 * The booking can still be called off: the contract is open and the unit never
 * left the hub (or was never assigned). Mirrors fleet's pre-pickup cancel gate;
 * past it, a rental ends through a return.
 */
export const canCancelRental = (
  rental: Pick<RentalListItem, 'status' | 'handoverStatus'>
): boolean =>
  [
    RentalStatus.PRE_ORDER,
    RentalStatus.REQUEST_BOOKING,
    RentalStatus.ACTIVE,
  ].includes(rental.status) &&
  (rental.handoverStatus == null ||
    rental.handoverStatus === HandoverStatus.WAITING_HANDOVER);

/** "REPO-19 · In Progress" — the case a rental row links to. */
export const repossessionLinkLabel = (
  repossession: NonNullable<RentalListItem['repossession']>
): string =>
  `${repossession.code} · ${repossession.status
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')}`;

export const showsEscalationPill = (
  status: EscalationStatus | null | undefined
): boolean => Boolean(status) && status !== EscalationStatus.NORMAL;

/**
 * Text colour for the arrears figure, tracking the escalation rung rather than
 * the day count — the rung is what decides whether the unit gets shut down, so
 * it is the honest severity signal. Mirrors the `escalationStatus` pill
 * palette in `components/Status`.
 */
export const escalationToneClass = (
  status: EscalationStatus | null | undefined
): string => {
  switch (status) {
    case EscalationStatus.WARNED:
      return 'text-[#F6B51E]';
    case EscalationStatus.SHUTDOWN_PENDING:
      return 'text-[#FA7319]';
    case EscalationStatus.SHUTDOWN:
    case EscalationStatus.REPO_REQUESTED:
      return 'text-[#FB3748]';
    // Held by ops on purpose — the debt stands but no consequence is coming.
    case EscalationStatus.HOLD:
      return 'text-gray-500';
    default:
      return 'text-gray-900';
  }
};

/**
 * "3 hari · Rp 234.000" — the two halves of an arrear read together, because
 * the money is the actionable part and days alone say nothing about size.
 * Null when the rental owes nothing, so the caller can render a dash.
 */
export const formatArrears = (
  arrears: RentalArrears | null | undefined
): string | null => {
  const days = arrears?.days ?? 0;
  const amount = arrears?.amount ?? 0;
  if (days <= 0 && amount <= 0) return null;

  return [
    days > 0 ? `${days} hari` : '',
    amount > 0 ? formatRupiah(amount) : '',
  ]
    .filter(Boolean)
    .join(' · ');
};

/**
 * "4 Aug 2026, 15:00" — the moment today's unpaid charge becomes arrears,
 * rendered in the browser's timezone. Null when the server sent no cutoff.
 */
export const formatArrearsCutoff = (
  startAt: string | null | undefined
): string | null => {
  if (!startAt) return null;
  const parsed = dayjs(startAt);
  return parsed.isValid() ? parsed.format('D MMM YYYY, HH:mm') : null;
};

export const arrearsCutoffToneClass = (warningActive?: boolean): string =>
  warningActive ? 'font-medium text-[#F6B51E]' : 'text-gray-500';

/**
 * Days the unit has been out: handover until the rental ended, or until today
 * while it is still running. Zero before handover.
 */
export const computeDurationDays = (
  handoverDate: string | null,
  endDate: string | null
): number => {
  if (!handoverDate) return 0;
  const start = dayjs(handoverDate);
  if (!start.isValid()) return 0;
  const end = endDate && dayjs(endDate).isValid() ? dayjs(endDate) : dayjs();
  return Math.max(end.startOf('day').diff(start.startOf('day'), 'day'), 0);
};

/** The three fields the deposit figures are derived from. */
type DepositSource = Pick<
  RentalDetail,
  'depositPaidTotal' | 'plan' | 'depositInstallment' | 'termsSnapshot'
>;

/**
 * Deposit collected so far.
 *
 * Prefers the server's `depositPaidTotal` — it is the figure settlement is
 * computed from, so the screen should not disagree with it. Falls back to the
 * DP plus the instalments settled to date, which the fixed-size detail carries
 * as `plan.installmentAmount` and `depositInstallment.paidDays`. Counting the
 * DP alone would leave the figure frozen for the whole rental, since
 * instalments are what actually pay the deposit down.
 *
 * `paidDays`, not `coveredDays`: this is money already moved into the deposit,
 * and it feeds the settlement figures. A prepaid balance is the driver's until
 * the engine settles it, so projecting it here would overstate what Dash holds.
 */
export const computeDepositPaid = (detail: DepositSource): number => {
  if (typeof detail.depositPaidTotal === 'number') {
    return Math.max(detail.depositPaidTotal, 0);
  }

  const dp = detail.plan?.dpAmount ?? 0;
  const installments =
    (detail.depositInstallment?.paidDays ?? 0) *
    (detail.plan?.installmentAmount ?? 0);
  return Math.max(dp + installments, 0);
};

/**
 * Deposit still owed, or null when the terms snapshot carries no deposit — the
 * caller renders a dash rather than a misleading Rp 0.
 */
export const computeRemainingDeposit = (
  detail: DepositSource
): number | null => {
  const total = detail.termsSnapshot?.deposit;
  if (typeof total !== 'number') return null;
  return Math.max(total - computeDepositPaid(detail), 0);
};

/** "Electrum - H3" */
// Partial, not RentalVehicle: it reads only the two name fields, and the
// dashboard's payments feed carries a trimmed vehicle block with no id.
export const formatVehicleLabel = (
  vehicle: Partial<RentalVehicle> | null
): string | null => {
  if (!vehicle) return null;
  const parts = [vehicle.oemName, vehicle.modelName].filter(Boolean);
  return parts.length ? parts.join(' - ') : null;
};

export const formatDate = (value?: string | null, fallback = '-'): string => {
  if (!value) return fallback;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : fallback;
};

/**
 * A `createdBy`/`paidBy` actor as ops should read it: the API ships
 * `driver:<uuid>` and `SYSTEM_BILLING`, neither of which belongs in a table
 * cell raw. Call sites keep the full value as a tooltip.
 */
export const formatActor = (value?: string | null, fallback = '-'): string => {
  if (!value) return fallback;
  if (value.startsWith('driver:')) return 'Driver';
  if (value === 'SYSTEM') return 'Sistem';
  if (value.startsWith('SYSTEM_')) {
    const job = value.slice('SYSTEM_'.length).replace(/_/g, ' ').toLowerCase();
    return `Sistem (${job})`;
  }
  return value;
};

export const formatTimelineDate = (value?: string | null): string => {
  if (!value) return '-';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('D MMM YYYY') : '-';
};

export const formatTimelineTime = (value?: string | null): string => {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() && value.includes('T') ? parsed.format('HH:mm') : '';
};

export interface TimelineEntry {
  key: string;
  date: string;
  time: string;
  description: string;
  /** Key into the `timelineEvent` Status variant. */
  chip: string;
  /** Sort key. */
  sortAt: number;
  /** Who caused it, already readable. Null when the API sent no actor. */
  actor: string | null;
}

/**
 * `created_by` arrives in three shapes and none of them are meant for a
 * screen: `SYSTEM_*` for the jobs, an ops email, and `driver:<id>` for
 * anything the mitra did in the app.
 */
const SYSTEM_ACTOR_LABEL: Record<string, string> = {
  SYSTEM_BILLING: 'Sistem · tagihan harian',
  SYSTEM_ESCALATION: 'Sistem · eskalasi',
  SYSTEM_EXPIRY_WATCHER: 'Sistem · pemantau kedaluwarsa',
  SYSTEM_CAPTURE: 'Sistem · pembayaran masuk',
  SYSTEM_PAYOUT_LISTENER: 'Sistem · payout',
  SYSTEM_PREORDER_CONVERSION: 'Sistem · konversi pre-order',
  SYSTEM_TENOR: 'Sistem · tenor',
};

/**
 * The actor as a person would say it. `driverName` comes from the rental, not
 * from the entry — the id in `driver:<id>` is not worth showing on its own.
 * Returns null when there is nothing to say, so the row renders no actor line
 * rather than an empty one.
 */
export const describeTimelineActor = (
  createdBy: string | null | undefined,
  driverName?: string | null
): string | null => {
  const raw = createdBy?.trim();
  if (!raw) return null;

  const known = SYSTEM_ACTOR_LABEL[raw];
  if (known) return known;
  // An unmapped SYSTEM_* is still a job, and saying so beats the raw constant.
  if (raw.startsWith('SYSTEM_')) return 'Sistem';
  if (raw.startsWith('driver:')) {
    return driverName ? `Mitra · ${driverName}` : 'Mitra';
  }
  return raw;
};

/**
 * Wording for a transition, keyed on `statusType` first.
 *
 * That ordering is load-bearing, not stylistic: CANCELLED is a valid value in
 * both RentalStatus and RentalPaymentStatus, so a from/to pair alone cannot
 * say whether the rental or the payment was cancelled. The API deliberately
 * ships raw transitions and leaves the language to us.
 */
const TRANSITION_COPY: Record<
  TimelineStatusType,
  Record<string, { description: string; chip: string }>
> = {
  [TimelineStatusType.RENTAL]: {
    PRE_ORDER: { description: 'Pre Order dibuat', chip: 'BOOKING' },
    REQUEST_BOOKING: {
      description: 'Unit dipesan — menunggu pembayaran DP',
      chip: 'PENDING',
    },
    ACTIVE: { description: 'DP dibayar — sewa dimulai', chip: 'START' },
    ENDED: { description: 'Sewa selesai — unit dikembalikan', chip: 'END' },
    COMPLETED: {
      description: 'Tenor RTO lunas — unit menjadi milik mitra',
      chip: 'END',
    },
    CANCELLED: { description: 'Sewa dibatalkan', chip: 'END' },
  },
  [TimelineStatusType.PAYMENT]: {
    PENDING_PAYMENT: { description: 'Menunggu pembayaran', chip: 'PENDING' },
    PAID: { description: 'Pembayaran berhasil', chip: 'PAID' },
    CANCELLED: { description: 'Pembayaran dibatalkan', chip: 'END' },
    REFUNDED: { description: 'Dana dikembalikan', chip: 'REFUNDED' },
  },
  [TimelineStatusType.ESCALATION]: {
    NORMAL: { description: 'Pembayaran kembali normal', chip: 'PAID' },
    WARNED: {
      description: 'Peringatan keterlambatan pembayaran',
      chip: 'OVERDUE',
    },
    SHUTDOWN_PENDING: {
      description: 'Menunggu shutdown manual oleh ops',
      chip: 'OVERDUE',
    },
    SHUTDOWN: { description: 'Unit dimatikan', chip: 'SHUTDOWN' },
    REPO_REQUESTED: {
      description: 'Permintaan penarikan unit diajukan',
      chip: 'SHUTDOWN',
    },
    HOLD: { description: 'Eskalasi ditahan oleh ops', chip: 'PENDING' },
  },
};

/**
 * Wording for a POSSESSION transition. A separate map from TRANSITION_COPY
 * because handover rows carry no `statusType` — and because the same word means
 * different things on the two dimensions: rental CANCELLED is a dead contract,
 * handover CANCELLED is a slot that was given back.
 */
const HANDOVER_TRANSITION_COPY: Record<
  string,
  { description: string; chip: string }
> = {
  WAITING_HANDOVER: {
    description: 'Unit disiapkan — menunggu diambil di hub',
    chip: 'BOOKING',
  },
  ACTIVE: { description: 'Unit diserahterimakan ke mitra', chip: 'START' },
  // The unit is still out, so this is not an ending — the chip stays a
  // scheduling one rather than END.
  WAITING_RETURN: {
    description: 'Pengembalian dijadwalkan — unit masih di mitra',
    chip: 'BOOKING',
  },
  RETURNED: { description: 'Unit dikembalikan ke hub', chip: 'END' },
  RELEASED: {
    description: 'Unit lepas ke mitra (tenor RTO lunas)',
    chip: 'END',
  },
  NO_SHOW: { description: 'Mitra tidak datang saat jadwal', chip: 'SHUTDOWN' },
  CANCELLED: { description: 'Jadwal serah terima dibatalkan', chip: 'END' },
};

export const describeHandoverTimelineEntry = (
  entry: Pick<HandoverTimelineEntry, 'fromStatus' | 'toStatus'>
): { description: string; chip: string } => {
  const known = HANDOVER_TRANSITION_COPY[entry.toStatus];
  if (known) return known;

  const from = entry.fromStatus ? `${entry.fromStatus} → ` : '';
  return { description: `${from}${entry.toStatus}`, chip: 'PENDING' };
};

/** Falls back to the raw transition so an unmapped status still reads sensibly. */
export const describeTimelineEntry = (
  entry: Pick<RentalTimelineEntry, 'statusType' | 'fromStatus' | 'toStatus'>
): { description: string; chip: string } => {
  const known = TRANSITION_COPY[entry.statusType]?.[entry.toStatus];
  if (known) return known;

  const from = entry.fromStatus ? `${entry.fromStatus} → ` : '';
  return { description: `${from}${entry.toStatus}`, chip: 'PENDING' };
};

/**
 * The detail screen's activity feed. The API returns oldest-first; the design
 * shows newest-first.
 */
export const buildActivityTimeline = (
  entries: RentalTimelineEntry[] | null | undefined,
  driverName?: string | null,
  reassignments?: RentalReassignment[] | null
): TimelineEntry[] =>
  [
    ...(entries ?? []).map((entry, index) => {
      const { description, chip } = describeTimelineEntry(entry);
      return {
        key: entry.ID ?? `timeline-${index}`,
        date: formatTimelineDate(entry.createdAt),
        time: formatTimelineTime(entry.createdAt),
        description,
        chip,
        sortAt: dayjs(entry.createdAt).valueOf() || 0,
        actor: describeTimelineActor(entry.createdBy, driverName),
      };
    }),
    ...(reassignments ?? []).map((swap) => ({
      key: `reassign-${swap.ID}`,
      date: formatTimelineDate(swap.createdAt),
      time: formatTimelineTime(swap.createdAt),
      description: describeReassignment(swap),
      chip: 'REASSIGN',
      sortAt: dayjs(swap.createdAt).valueOf() || 0,
      actor: describeTimelineActor(swap.createdBy, driverName),
    })),
  ].sort((a, b) => b.sortAt - a.sortAt);

/** "Unit diganti B 1 → B 2", plus the override, what it waived and the tenor. */
export const describeReassignment = (swap: RentalReassignment): string => {
  const parts = [
    `Unit diganti ${swap.fromVehicle.platNomor} → ${swap.toVehicle.platNomor}`,
  ];
  if (swap.overrideReason) parts.push(`override: ${swap.overrideReason}`);
  if (swap.waived?.length) {
    parts.push(
      `dilewati: ${swap.waived.map(describePackageVehicleReason).join('; ')}`
    );
  }
  if (swap.tenorBefore !== null && swap.tenorAfter !== null) {
    parts.push(`tenor ${swap.tenorBefore} → ${swap.tenorAfter} hari`);
  }
  return parts.join(' · ');
};

/**
 * Which stage of the rental issued a copy of the agreement. The wording is the
 * same vocabulary the handover legs use ("Serah Terima" / "Pengembalian") so
 * one screen does not name the same moment two ways.
 */
export const RENTAL_DOCUMENT_STAGE_LABEL: Record<RentalDocumentStage, string> =
  {
    BOOKING: 'Booking',
    HANDOVER: 'Serah Terima',
    RETURN: 'Pengembalian',
  };

/**
 * Only a recorded pickup has a checklist to correct — the handover row exists
 * from DP capture, but its checklist lands when the unit leaves the hub.
 */
export const canCorrectHandoverChecklist = (
  detail: Pick<RentalDetail, 'handover'>
): boolean => Boolean(detail.handover?.ID && detail.handover.handoverDate);

/** "Charger: Tidak ada → Ada" — one flipped answer, as the summary lists it. */
export const describeChecklistChange = (change: {
  label: string;
  present: boolean;
}): string =>
  `${change.label}: ${change.present ? 'Tidak ada → Ada' : 'Ada → Tidak ada'}`;

/** The reissued documents, worded as the Dokumen section names them. */
export const describeReissuedDocuments = (
  documents: CorrectedDocument[]
): string => {
  const agreements = documents
    .filter((d): d is Exclude<CorrectedDocument, 'BAST'> => d !== 'BAST')
    .map((d) => RENTAL_DOCUMENT_STAGE_LABEL[d]);
  return [
    documents.includes('BAST') ? 'BAST' : '',
    agreements.length
      ? `Perjanjian Sewa Kontrak (${agreements.join(' & ')})`
      : '',
  ]
    .filter(Boolean)
    .join(' dan ');
};

/**
 * How long after a correction a pending reissue is still worth waiting on. A
 * render takes a minute or two; past this the task is presumed lost, and the
 * rows offer the copy they hold rather than a spinner forever.
 */
export const REISSUE_WAIT_MS = 10 * 60_000;

/**
 * The documents a checklist correction is still re-rendering, as the server
 * reports them, or none once the wait has run out.
 */
export const activeReissue = (
  documents: Pick<RentalDocuments, 'reissue'> | null | undefined,
  now = Date.now()
): CorrectedDocument[] => {
  const reissue = documents?.reissue;
  if (!reissue?.requestedAt || reissue.pending.length === 0) return [];
  const age = now - Date.parse(reissue.requestedAt);
  return age < REISSUE_WAIT_MS ? reissue.pending : [];
};

/**
 * The badge that says what KIND of number `estimatedNet` is.
 *
 * Never render the figure without it. `ESTIMATE` is a ceiling — deductions are
 * unknown until the unit is back, and the amount moves every day as deposit
 * instalments settle — so an unlabelled number invites ops to quote a
 * mid-rental plafon to a driver as if it were settled.
 */
export const REFUNDABLE_STAGE_LABEL: Record<string, string> = {
  PRE_HANDOVER: 'Refund Penuh',
  ESTIMATE: 'Estimasi',
  FINAL: 'Final',
};

export const refundableStageToneClass = (stage?: string): string =>
  stage === 'ESTIMATE'
    ? 'bg-[#FEF6E0] text-[#8A6100]'
    : 'bg-[#E8F5E9] text-[#1B5E20]';

export type DepositRow = {
  label: string;
  amount: number;
  /** Rendered with a leading minus and a muted tone. */
  negative?: boolean;
  hint?: string;
};

/**
 * The arithmetic behind `estimatedNet`, as rows to render.
 *
 * Subtractions appear only when they actually bit, so an ordinary rental shows
 * two lines instead of a column of zeros. A total whose subtractions are
 * invisible is a figure nobody can check, which is why they are listed at all —
 * `Potongan Kerusakan` is deliberately repeated here even though the Deduksi
 * Kerusakan section itemizes it: there it is the list, here it is the sum.
 */
export const buildDepositRows = (
  refundable: NonNullable<RentalDetail['refundable']>
): DepositRow[] => {
  const rows: DepositRow[] = [
    { label: 'Deposit Terbayar', amount: refundable.depositPaid },
    { label: 'Saldo Prepaid', amount: refundable.prepaidBalance },
  ];
  if (refundable.nonRefundable > 0) {
    rows.push({
      label: 'Deposit Tidak Kembali',
      amount: refundable.nonRefundable,
      negative: true,
    });
  }
  // Never refunded — a PSP cost already incurred. Shown because the day the Dash
  // discount drops, a driver pays 203.885 and receives 197.500, and an
  // unexplained gap becomes a support ticket.
  if (refundable.adminFeePaid > 0) {
    rows.push({
      label: 'Biaya Admin',
      amount: refundable.adminFeePaid,
      hint: 'hangus',
    });
  }
  if (refundable.deductions > 0) {
    rows.push({
      label: 'Potongan Kerusakan',
      amount: refundable.deductions,
      negative: true,
    });
  }
  if (refundable.mdrFee > 0) {
    rows.push({
      label: 'Biaya Transfer',
      amount: refundable.mdrFee,
      negative: true,
    });
  }
  return rows;
};

/**
 * Whether this rental has a deposit story worth a section at all.
 *
 * A fresh unpaid booking has none — every figure is 0, and rendering the section
 * would show an Rp0 estimate as though it meant something.
 */
export const hasDepositStory = (
  refundable: RentalDetail['refundable']
): boolean =>
  !!refundable &&
  (refundable.depositPaid > 0 ||
    refundable.prepaidBalance > 0 ||
    refundable.estimatedGross > 0);

/**
 * Badge tone for a refund's payout state. NOT_INSTRUCTED is the loud one — the
 * only state the retry action can act on — while FAILED is just as unpaid but
 * not retryable from here, so it reads as a warning.
 */
export const refundToneClass = (
  status: RefundPayoutStatus | null | undefined
): string => {
  switch (status) {
    case RefundPayoutStatus.NOT_INSTRUCTED:
      return 'bg-[#FFE5E7] text-[#B3131F]';
    case RefundPayoutStatus.FAILED:
      return 'bg-[#FEF6E0] text-[#8A6100]';
    case RefundPayoutStatus.PENDING:
      return 'bg-[#EAF2FF] text-[#1F4B99]';
    case RefundPayoutStatus.COMPLETED:
      return 'bg-[#E8F5E9] text-[#1B5E20]';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

export const refundStatusLabel = (
  status: RefundPayoutStatus | null | undefined
): string => (status && REFUND_PAYOUT_STATUS_LABEL[status]) || 'Refund';

/**
 * Whether a refund needs somebody to look at it. NOT_INSTRUCTED and FAILED both
 * mean the driver has not been paid; PENDING and COMPLETED are on their way and
 * need no banner.
 */
export const needsRefundAttention = (
  refund: { payoutStatus: RefundPayoutStatus | null } | null | undefined
): boolean =>
  refund?.payoutStatus === RefundPayoutStatus.NOT_INSTRUCTED ||
  refund?.payoutStatus === RefundPayoutStatus.FAILED;

/**
 * Whether ops can re-instruct this refund — every refund that needs attention:
 * NOT_INSTRUCTED replays its own id, FAILED gets a new attempt with a new id.
 * Needs the payout id, which collection rows from older backends lack.
 */
export const canRetryRefund = <
  T extends { payoutID?: string; payoutStatus: RefundPayoutStatus | null },
>(
  refund: T | null | undefined
): refund is T & { payoutID: string } =>
  Boolean(refund?.payoutID) && needsRefundAttention(refund);

/** Only a rent payment still waiting to be paid; a DP goes with its booking. */
export const canCancelPayment = (collection: {
  status: CollectionStatus;
  purpose: CollectionPurpose;
}): boolean =>
  collection.status === CollectionStatus.PENDING &&
  collection.purpose === CollectionPurpose.PREPAY;

/** The Overdue tab's free day range, as typed. Blank means that end is open. */
export interface DayRangeInput {
  min: string;
  max: string;
}

/** Parsed range, or null when a bound is not a whole number or min exceeds max. */
export const parseDayRange = (
  input: DayRangeInput
): { arrearsDaysMin?: number; arrearsDaysMax?: number } | null => {
  const read = (v: string): number | undefined | null => {
    if (v.trim() === '') return undefined;
    const n = Number(v);
    return Number.isInteger(n) && n >= 0 ? n : null;
  };
  const min = read(input.min);
  const max = read(input.max);
  if (min === null || max === null) return null;
  if (min !== undefined && max !== undefined && min > max) return null;
  return {
    ...(min !== undefined ? { arrearsDaysMin: min } : {}),
    ...(max !== undefined ? { arrearsDaysMax: max } : {}),
  };
};

/**
 * "Dibayar Oleh" on a waived day. `reason` is coupon's idempotency key today,
 * so it is shown only once it reads as a label ("Referral DASH24").
 */
export const waivedDayLabel = (
  chargeDate: string,
  waivers: RentalWaiver[] = []
): string => {
  const referral = waivers.find(
    (waiver) =>
      waiver.source === 'REFERRAL' &&
      waiver.waiverDate.slice(0, 10) === chargeDate.slice(0, 10)
  );
  if (!referral) return 'Hari gratis';
  return referral.reason?.startsWith('Referral ')
    ? referral.reason
    : 'Referral';
};

/**
 * "Voucher RENTX7K2 −Rp25.000" under a collection's amount; null without one.
 * Not "Voucher referral": the code alone does not say where the voucher came from.
 */
export const collectionVoucherLabel = (collection: {
  couponCode?: string | null;
  discountAmount?: number;
}): string | null => {
  if (!collection.couponCode) return null;
  const discount = collection.discountAmount ?? 0;
  return discount > 0
    ? `Voucher ${collection.couponCode} \u2212${formatRupiah(discount, '-', { space: false })}`
    : `Voucher ${collection.couponCode}`;
};

/**
 * Free rent days are day-of-month numbers ([15, 30]), so a bare "15 & 30"
 * reads like a count. Null when the package grants none — the caller dashes.
 */
export const formatFreeDayDates = (dates?: number[] | null): string | null =>
  dates?.length ? `Tgl ${[...dates].sort((a, b) => a - b).join(' & ')}` : null;
